/**
 * Markdown field component
 * Markdown editor with preview support
 */

import React, { useState } from 'react';
import { FieldSchema, ValidationResult, getHelpText } from '@/config/fieldConfig';

export interface MarkdownFieldProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
}

export const MarkdownField: React.FC<MarkdownFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const placeholder = typeof schema.placeholder === 'function' ? schema.placeholder() : schema.placeholder;
  const help = getHelpText(schema.help);
  const rows = schema.rows || 10;

  const borderClass = error && !error.isWarning
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : error?.isWarning
    ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  // Simple markdown to HTML conversion (basic implementation)
  const renderMarkdown = (text: string): string => {
    if (!text) return '';

    return text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded my-2 overflow-x-auto"><code>$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p class="my-2">')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>');
  };

  return (
    <div className="field-wrapper">
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={`field-${name}`} className="block text-sm font-medium text-gray-700">
          {label}
          {schema.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          {showPreview ? 'Edit' : 'Preview'}
        </button>
      </div>

      <div className="editor-container">
        {!showPreview ? (
          <textarea
            id={`field-${name}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={schema.readonly}
            rows={rows}
            className={`
              w-full px-3 py-2 rounded-lg border
              focus:outline-none focus:ring-2
              disabled:bg-gray-100 disabled:cursor-not-allowed
              read-only:bg-gray-50 read-only:cursor-not-allowed
              resize-y font-mono text-sm
              ${borderClass}
            `}
          />
        ) : (
          <div
            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 min-h-[200px] overflow-auto prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value || '') }}
          />
        )}
      </div>

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}

      {help && !error && (
        <div className="text-xs text-gray-500 mt-1">
          {help}
        </div>
      )}
    </div>
  );
};

export default MarkdownField;
